import urljoin from 'url-join';
import { api } from '@/shared/services/baseApiClient';
import { config } from '@/config/config';
import {
  InstalledApp,
  PublishedApp,
  PrivateApp,
  CreatePrivateAppViewModel,
  UpdatePrivateAppViewModel,
  InstallAppViewModel,
  CreatedPrivateAppViewModel,
  InstallAppSuccessViewModel,
} from '../models';

class AppsApiService {
  private version = 'v1';

  public listPublished(workspaceId: string): Promise<PublishedApp[]> {
    return api.send<PublishedApp[]>(
      'GET',
      urljoin(config().appsHost, this.version, `/workspaces/${workspaceId}/apps/published`),
    );
  }

  public listPrivate(workspaceId: string): Promise<PrivateApp[]> {
    return api.send<PrivateApp[]>(
      'GET',
      urljoin(config().appsHost, this.version, `workspaces/${workspaceId}/apps/private`),
    );
  }

  public createPrivateApp(workspaceId: string, data: CreatePrivateAppViewModel): Promise<CreatedPrivateAppViewModel> {
    return api.send<CreatedPrivateAppViewModel>(
      'POST',
      urljoin(config().appsHost, this.version, `workspaces/${workspaceId}/apps/private`),
      data,
    );
  }

  public updatePrivateApp(workspaceId: string, appId: string, data: UpdatePrivateAppViewModel): Promise<PrivateApp> {
    return api.send<PrivateApp>(
      'PUT',
      urljoin(config().appsHost, this.version, `workspaces/${workspaceId}/apps/private/${appId}`),
      data,
    );
  }

  public getPrivateApp(workspaceId: string, appId: string): Promise<PrivateApp> {
    return api.send<PrivateApp>(
      'GET',
      urljoin(config().appsHost, this.version, `workspaces/${workspaceId}/apps/private/${appId}`),
    );
  }

  public async getCapableApps(workspaceId: string, capabilityList: string[]): Promise<InstalledApp[]> {
    return api.send<InstalledApp[]>(
      'GET',
      urljoin(
        config().appsHost,
        this.version,
        `workspaces/${workspaceId}/apps?capabilities=${capabilityList.join(',')}`,
      ),
    );
  }

  public getInstalledApps(workspaceId: string): Promise<InstalledApp[]> {
    return api.send<InstalledApp[]>(
      'GET',
      urljoin(config().appsHost, this.version, `workspaces/${workspaceId}/apps/installed`),
    );
  }

  public getInstalledApp(workspaceId: string, appId: string): Promise<InstalledApp> {
    const url = urljoin(config().appsHost, this.version, 'workspaces', workspaceId, `apps/installed?appId=${appId}`);
    return api.send<InstalledApp>('GET', url);
  }

  public install(workspaceId: string, data: InstallAppViewModel): Promise<InstallAppSuccessViewModel> {
    return api.send<InstallAppSuccessViewModel>(
      'POST',
      urljoin(config().appsHost, this.version, `workspaces/${workspaceId}/apps/installed`),
      data,
    );
  }

  public uninstall(workspaceId: string, installedAppId: string): Promise<void> {
    return api.send(
      'DELETE',
      urljoin(config().appsHost, this.version, `workspaces/${workspaceId}/apps/installed/${installedAppId}`),
    );
  }
}

export const appsApi = new AppsApiService();
